import { Deserializer } from "jsonapi-serializer";
import { INTERNAL_SERVER_ERROR, NO_CONTENT } from "../constants/response-status-codes";
import { genericError } from "../models/GenericError";
import { ListQuery } from "../models/ListQuery";
import { ApElLockWrapper, LockModel } from "../models/Lock";
import { Name } from "../models/Name";
import { PaginationModel } from "../models/Pagination";
import { StoreEntityResult } from "../models/services/StoreEntityResult";
import { APIConnector } from "./api/APIConnector";
import { getBiometricDeviceByAssignedDeviceImprox } from "./biometricService";
import { DEVICE_CATEGORIES } from "../constants/device-constants";
import { processServerError, getConstraintViolation } from "./utilities/ServiceError";
import { getDeviceLicenceStatus } from "./utilities/StatusBuilder";
import { createPagination } from "./utilities/paginationBuilder";
import { createPartialURL, getFilterClause } from "./utilities/urlBuilder";
import { Translator } from "../contexts/translator";
import { TranslationKeys } from "../contexts/TranslationKeys";

const apiConnector = new APIConnector();

export const getLocks = async (translator: Translator, listParameters?: ListQuery,): Promise<ApElLockWrapper> => {
    try {
        const filterCategory = `unitType.utCategory=in=(${DEVICE_CATEGORIES.EL_LOCK_ENTRY},${DEVICE_CATEGORIES.EL_LOCK_EXIT},${DEVICE_CATEGORIES.LOCK})`;
        let filter: string = getFilterClause(listParameters?.filters, listParameters?.searchTerm);
        filter = filter ? `${filter};${filterCategory}` : `&filter=${filterCategory}`; 
        const partialUrl = createPartialURL(listParameters?.goToPage, listParameters?.pageSize, listParameters?.sortBy, filter);
        const includes = "include=unitType,site,profile,controller,location";
        const terminalFields = "fields[terminal]=devName,devFixedAddr,musteringPoint,devIpAddress,devEnabled,devCommsStatus,deviceImprox,location,controller,unitType,site,profile,interpretedLicense,devReaderSerial,hasPendingLicense";
        const locationFields = "fields[location]=locName";
        const unitTypeFields = "fields[unitType]=trnName,utCategory";
        const siteFields = "fields[site]=siteName";
        const profileFields = "fields[profile]=trnName,profileSysnameType";
        const controllerFields = "fields[controller]=devName,devFixedAddr";
        const url = `terminal?${includes}&${terminalFields}&${locationFields}&${unitTypeFields}&${siteFields}&${profileFields}&${controllerFields}${partialUrl}`;
        const response = await apiConnector.fetchData(url);
        const json = await response.json();
        const pagination = createPagination(json.meta);

        return new Promise((resolve, reject) => {
            if (json.errors) {
                return reject({
                    status: response.status,
                    message: getConstraintViolation(json.errors[0].detail),
                });
            }

            new Deserializer().deserialize(json, (error: any, locks: any[]) => {
                if (error) {
                    return reject({ status: response.status, message: genericError });
                }

                const rows: LockModel[] = locks.map((lock) => {
                    return {
                        id: lock['id'],
                        name: lock['dev-name'],
                        fixedAddress: lock['dev-fixed-addr'],
                        ipAddress: lock['dev-ip-address'] ? lock['dev-ip-address'] : '-',
                        site: lock['site']['site-name'],
                        preset: lock['profile']['trn-name'],
                        type: lock['unit-type']['trn-name'],
                        controller: lock['controller']['dev-name'],
                        controllerAddress: lock['controller']['dev-fixed-addr'],
                        door: lock['location'] ? lock['location']['loc-name'] : '',
                        enabled: lock['dev-enabled'],
                        devCommsStatus: lock['dev-comms-status'],
                        status: getDeviceLicenceStatus(true, lock['dev-enabled'], lock['dev-comms-status']),
                        musteringPoint: lock['mustering-point'] == 1 ? true : false,
                        musteringPointLabel: lock['mustering-point'] == 1 ? translator.byKey(TranslationKeys.yes) : translator.byKey(TranslationKeys.no),
                        devLicense: lock['interpreted-license'] ? lock['interpreted-license'] : lock['has-pending-license'] ? '' : '-', //If we have a pending license we will show an icon
                        devReaderSerial: lock['dev-reader-serial'] ? lock['dev-reader-serial'] : '-',
                        pendingLicense: lock['has-pending-license']
                    }
                })

                return resolve({ rows, pagination });
            });
            
        });
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
};

export const getLockById = async (id: string): Promise<LockModel> => {
    try {
        const includes = "include=controller,unitType,site,profile,location,locationType,deviceImprox,modeType,threatLevel";
        const terminalFields = "fields[terminal]=devName,devDesc,devFixedAddr,devSla,devDirection,musteringPoint,devIpAddress,devFwVersion,devEnabled,devCommsStatus,totalActions,totalParameters,totalInputs,deviceImprox,location,locationType,controller,unitType,site,profile,modeType,threatLevel,interpretedLicense,devReaderSerial";
        const locationFields = "fields[location]=locName";
        const unitTypeFields = "fields[unitType]=trnName,utCategory";
        const siteFields = "fields[site]=siteName";
        const profileFields = "fields[profile]=trnName,profileSysnameType";
        const controllerFields = "fields[controller]=devName,devFixedAddr";
        const locationTypeFields = "fields[locationType]=ltName";
        const modeTypeFields = "fields[modeType]=trnName";
        const threatLevelFields = "fields[threatLevel]=tlName";
        const url = `terminal/${id}?${includes}&${terminalFields}&${locationFields}&${unitTypeFields}&${siteFields}&${profileFields}&${controllerFields}&${locationTypeFields}&${modeTypeFields}&${threatLevelFields}`;
        const response = await apiConnector.fetchData(url);
        const json = await response.json();
        const biometricValues = await getBiometricDeviceByAssignedDeviceImprox(id);

        return new Promise((resolve, reject) => {
            if (json.errors) {
                return reject({
                    status: response.status,
                    message: getConstraintViolation(json.errors[0].detail),
                });
            }

            new Deserializer().deserialize(json, (error: any, lock: any[]) => {
                if (error) {
                    return reject({
                        status: response.status,
                        message: genericError,
                    });
                }

                const builtLock = {
                    id: lock['id'],
                    name: lock['dev-name'],
                    fixedAddress: lock['dev-fixed-addr'],
                    ipAddress: lock['dev-ip-address'] ? lock['dev-ip-address'] : '-',
                    site: lock['site']['site-name'],
                    siteId: lock['site']['id'],
                    preset: lock['profile']['trn-name'],
                    type: lock['unit-type']['trn-name'],
                    typeId: lock['unit-type']['id'],
                    controller: lock['controller']['dev-name'],
                    controllerAddress: lock['controller']['dev-fixed-addr'],
                    door: lock['location'] ? lock['location']['loc-name'] : '',
                    defaultMode: lock['mode-type']['trn-name'],
                    defaultModeId: lock['mode-type']['id'],
                    functionId: lock['location-type']['id'],
                    function: lock['location-type']['lt-name'],
                    threatLevelId: lock['threat-level']['id'],
                    threatLevel: lock['threat-level']['tl-name'],
                    position: !!lock['dev-direction'] ? 'Exit' : 'Entry',
                    version: lock['dev-fw-version'],
                    enabled: lock['dev-enabled'],
                    presetId: lock['profile']['id'],
                    logicalAddress: lock['dev-sla'],
                    description: lock['dev-desc'] ?? '',
                    terminalId: lock['id'],
                    totalActions: lock['total-actions'],
                    totalParameters: lock['total-parameters'],
                    totalInputs: lock['total-inputs'],
                    positionKey: lock['dev-direction'],
                    biometricInput: biometricValues != null ? biometricValues.value : '',
                    unlinkBiometricInput: biometricValues != null ? biometricValues.value : '',
                    profileSysNameType: lock['profile']['profile-sysname-type'],
                    musteringPoint: lock['mustering-point'] == 1 ? true : false,
                    devLicense: lock['interpreted-license'] ? lock['interpreted-license'] : '-',
                    devReaderSerial: lock['dev-reader-serial'] ? lock['dev-reader-serial'] : '-',
                };

                return resolve(builtLock);
            });
        });
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
};


export const getLocksNamesWithPagination = async (listQuery?: ListQuery): Promise<{ rows: Name[], pagination: PaginationModel }> => {
    try {
        const filterCategory = `unitType.utCategory=in=(${DEVICE_CATEGORIES.EL_LOCK_ENTRY},${DEVICE_CATEGORIES.EL_LOCK_EXIT},${DEVICE_CATEGORIES.LOCK})`;
        let filters: string = getFilterClause(listQuery?.filters, listQuery?.searchTerm);
        filters = filters ? `${filters};${filterCategory}` : `&filter=${filterCategory}`;
        const paginationString = createPartialURL(listQuery?.goToPage, listQuery?.pageSize, listQuery?.sortBy, filters);
        const includes = `include=deviceImprox`;
        const fieldsTerminal = `&fields[terminal]=devName`;
        const url = `terminal?${includes}${fieldsTerminal}${paginationString}`;
        const response = await apiConnector.fetchData(url);
        const json = await response.json();
        const newPagination = createPagination(json.meta);

        return new Promise((resolve, reject) => {
            if (json.errors) {
                return reject({ status: response.status, message: getConstraintViolation(json.errors[0].detail) })
            }
            new Deserializer().deserialize(json, (err: any, items: any[]) => {
                if (err) {
                    return reject({ status: response.status, message: genericError });
                }

                const readers: Name[] = items.map((lock): Name => {
                    return {
                        value: lock['id'],
                        label: lock['dev-name']
                    };
                });

                return resolve({ rows: readers, pagination: newPagination });
            })

        })
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
}

export const storeLock = async (lock, createLock = true): Promise<StoreEntityResult> => {
    try {
        const checkAssignedDevice = !lock.biometricInput && lock.unlinkBiometricInput ? true : false;
        const newRecord = {
            data: {
                type: 'terminal',
                id: lock.id,
                attributes: {
                    devDesc: lock.description,
                    devEnabled: !!lock.enabled,
                    devName: lock.name,
                    selectedProfileId: lock.selectedPresetId,
                    musteringPoint: !!lock.musteringPoint
                },
                relationships: {
                    profile: {
                        data: {
                            type: "profile",
                            id: lock.presetId
                        }
                    },
                    threatLevel: {
                        data: {
                            type: "threatLevel",
                            id: lock.threatLevelId
                        }
                    },
                    linkBioAddress: {
                        data: lock.biometricInput ? {
                            type: "bioAddress",
                            id: lock.biometricInput
                        } : undefined
                    },
                    unLinkBioAddress: {
                        data: checkAssignedDevice ? {
                            type: "bioAddress",
                            id: lock.unlinkBiometricInput
                        } : undefined
                    },
                    locationType: {
                        data: {
                            type: 'locationType',
                            id: lock.functionId
                        },
                    },
                    modeType: {
                        data: {
                            type: 'modeType',
                            id: lock.defaultModeId
                        },
                    }
                }
            }
        }

        const url = `terminal${createLock ? '' : `/${newRecord.data.id}`}`;
        let response: Response;

        await Promise
            .all([apiConnector.sendData(url, createLock, newRecord),])
            .then(data => {
                response = data[0];
            }).catch(error => {
                return new Promise((resolve, reject) => {
                    return reject(processServerError(error));
                })
            });

        return new Promise<StoreEntityResult>(async (resolve, reject) => {
            if (response) {
                if (createLock) {
                    const content = await response.json();
                    if (content.errors) {
                        return reject({ status: response.status, message: getConstraintViolation(content.errors[0].detail) })
                    }

                    new Deserializer().deserialize(content, (error, newLock) => {
                        if (error) {
                            return reject({ status: response.status, id: newLock.id, success: false });
                        }
                        return resolve({ id: newLock.id, success: true });
                    })

                }
                if (response.status === NO_CONTENT) {
                    return resolve({ id: lock.id, success: true })
                }

                const content = await response.json();
                return reject({ status: response.status, id: lock.id, success: false, message: content.errors.map(error => getConstraintViolation(error.detail)) });
            }
            return reject({ status: INTERNAL_SERVER_ERROR, id: lock.id, success: false, message: genericError });

        })
    } catch (error) {
        return new Promise((resolve, reject) => {
            return reject(processServerError(error));
        });
    }
}